<template>
  <div class="card">
    <a-collapse
        v-model:activeKey="mobileOpenKey"
        :bordered="false"
        class="faq-mobile faq-mobile__outer"
        expand-icon-position="end"
    >
      <a-collapse-panel key="menu" class="faq-mobile__panel">
        <template #header>
          <div class="faq-mobile__header">
            <div class="faq-mobile__title">{{ currentLabel }}</div>
            <UnorderedListOutlined
                v-if="!isMobileMenuOpen"
                class="faq-mobile__menu-icon"
            />
          </div>
        </template>

        <div class="faq-nav">
          <div v-for="item in faqNav" :key="item.block" class="faq-nav__row">
            <a-collapse
                v-if="item.children"
                :activeKey="openGroup === item.block ? [item.block] : []"
                :bordered="false"
                class="faq-nav__collapse"
                @change="(keys) => onGroupToggle(item.block, keys)"
            >
              <template #expandIcon="{ isActive }">
                <UpOutlined v-if="isActive" />
                <DownOutlined v-else />
              </template>

              <a-collapse-panel
                  :key="item.block"
                  :header="item.label"
                  class="faq-nav__panel"
              >
                <div
                    v-for="child in item.children"
                    :key="child.section"
                    class="faq-nav__child"
                    :class="{ 'is-active': isSectionActive(item.block, child.section) }"
                    @click.prevent="goTo(item.block, child.section)"
                >
                  {{ child.label }}
                </div>
              </a-collapse-panel>
            </a-collapse>

            <div
                v-else
                class="faq-nav__item"
                :class="{ 'is-active': isBlockActive(item.block) }"
                @click.prevent="goTo(item.block)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  DownOutlined,
  UnorderedListOutlined,
  UpOutlined,
} from "@ant-design/icons-vue";

import { faqNav } from "@/mixins/faqNav";
import {
  getFaqLabel,
  getFaqRouteLocation,
  normalizeFaqParams,
} from "@/views/FAQ/faqRouteHelper";

export default {
  name: "FaqMobileNav",
  components: {
    UpOutlined,
    DownOutlined,
    UnorderedListOutlined,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const routeState = computed(() => normalizeFaqParams(route.params));

    const mobileOpenKey = ref([]);
    const openGroup = ref(null);

    watch(
        routeState,
        (state) => {
          openGroup.value = state.block;
        },
        { immediate: true }
    );

    const isMobileMenuOpen = computed(() => mobileOpenKey.value.includes("menu"));

    const currentLabel = computed(() => {
      return getFaqLabel(routeState.value.block, routeState.value.section);
    });

    const goTo = (block, section = null) => {
      router.push(getFaqRouteLocation(block, section));
      mobileOpenKey.value = [];
    };

    const onGroupToggle = (block, keys) => {
      openGroup.value = keys.length ? block : null;
    };

    const isBlockActive = (block) => {
      return routeState.value.block === block && routeState.value.section === null;
    };

    const isSectionActive = (block, section) => {
      return routeState.value.block === block && routeState.value.section === section;
    };

    return {
      faqNav,
      mobileOpenKey,
      openGroup,
      isMobileMenuOpen,
      currentLabel,
      goTo,
      onGroupToggle,
      isBlockActive,
      isSectionActive,
    };
  },
};
</script>

<style scoped>
.card {
  margin-bottom: 0;
}

.faq-nav {
  display: block;
}

.faq-nav__item {
  display: block;
  padding: 10px 12px;
  color: var(--main-font);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__item.is-active {
  background: #f2f2f2;
  font-weight: 600;
}

.faq-nav__collapse :deep(.ant-collapse) {
  border: 0;
  background: #ffffff;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-item) {
  border: 0;
  background: #ffffff;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding: 10px 12px !important;
  background: #ffffff !important;
  color: var(--main-font) !important;
  font-size: 14px !important;
  line-height: 20px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding-inline-start: 12px !important;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header-text) {
  flex: 1 1 auto !important;
}

.faq-nav__collapse :deep(.ant-collapse-expand-icon) {
  order: 2 !important;
  margin-inline-start: auto !important;
  margin-inline-end: 0 !important;
  color: var(--main-font) !important;
  font-size: 12px;
}

.faq-nav__collapse :deep(.ant-collapse-header .ant-collapse-expand-icon) {
  padding: 0 !important;
}

.faq-nav__collapse :deep(.ant-collapse-item-active > .ant-collapse-header) {
  background: #ffffff !important;
}

.faq-nav__collapse :deep(.ant-collapse-header:hover) {
  background: #f7f7f7 !important;
  border-radius: 8px;
}

.faq-nav__collapse :deep(.ant-collapse-content) {
  margin-top: 8px !important;
  background: #ffffff !important;
  border: 0 !important;
  padding: 0 !important;
}

:deep(.ant-collapse-content-box) {
  padding: 0 !important;
  margin-top: 8px;
}

.faq-nav__child {
  display: block;
  padding: 8px 12px 8px 22px;
  color: var(--neutral-black-font);
  text-decoration: none;
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  border-radius: 8px;
}

.faq-nav__child.is-active {
  background: #f2f2f2;
  font-weight: 600;
}

.faq-nav__item:hover {
  background: #f7f7f7;
}

.faq-nav__child:hover {
  color: #595959;
}

.faq-mobile :deep(.ant-collapse) {
  border: 0;
  background: #ffffff;
}

.faq-mobile :deep(.ant-collapse-item) {
  border: 0;
  background: #ffffff;
}

.faq-mobile :deep(.ant-collapse-header) {
  padding: 0 !important;
  background: #ffffff !important;
}

.faq-mobile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.faq-mobile__title {
  color: var(--main-font);
  font-size: 16px;
  font-weight: 600;
}

.faq-mobile__menu-icon {
  font-size: 18px;
  color: var(--main-font);
}

.faq-mobile__outer :deep(> .ant-collapse-item:not(.ant-collapse-item-active) > .ant-collapse-header .ant-collapse-expand-icon),
.faq-mobile__outer :deep(> .ant-collapse-item:not(.ant-collapse-item-active) > .ant-collapse-header .ant-collapse-arrow) {
  display: none !important;
}

.faq-nav__collapse :deep(.ant-collapse) {
  border: 0 !important;
  background: transparent !important;
}

.faq-nav__collapse :deep(.ant-collapse-item) {
  border: 0 !important;
  background: transparent !important;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding: 10px 12px !important;
  background: transparent !important;
  color: var(--main-font) !important;
  font-size: 14px !important;
  line-height: 20px !important;
  display: flex !important;
  align-items: center !important;
  border-radius: 8px;
}

.faq-nav__collapse :deep(.ant-collapse-header:hover) {
  background: #f7f7f7 !important;
}

.faq-nav__collapse :deep(.ant-collapse-header-text) {
  flex: 1 1 auto !important;
}

.faq-nav__collapse :deep(.ant-collapse-expand-icon),
.faq-nav__collapse :deep(.ant-collapse-arrow) {
  margin-left: auto !important;
  color: var(--main-font) !important;
}

.faq-nav__collapse :deep(.ant-collapse-content) {
  margin-top: 0 !important;
  background: transparent !important;
  border: 0 !important;
}

.faq-nav__collapse :deep(.ant-collapse-content-box) {
  padding: 0 !important;
}
</style>
